'use client';

import { useEffect, useRef, useCallback } from 'react';
import { useAtomValue } from 'jotai';
import { track } from '@/utils/track';
import { isLoginAtom } from '@/store';
import { currentTabAtom } from '@/store/base';
import { getLocalStorageItem, setLocalStorageItem } from '@/utils/storage';

// 心跳打点相关常量
const HEARTBEAT_INTERVAL = 300; // 5分钟 = 300秒
const HEARTBEAT_STORAGE_KEY = 'heartbeat_last_time';

/**
 * 心跳打点 Hook
 * 
 * 功能特性：
 * 1. 每5分钟发送一次心跳打点
 * 2. 使用 localStorage 持久化上次打点时间，刷新页面不影响计时
 * 3. 监听页面可见性，tab 不可见时暂停，恢复时检查是否需要立即发送
 * 4. 使用递归 setTimeout 避免 setInterval 的累积误差
 * 
 * @returns void
 */
export function useHeartbeat() {
  const isLogin = useAtomValue(isLoginAtom);
  const currentTab = useAtomValue(currentTabAtom);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isVisibleRef = useRef(true);
  // 使用 ref 存储最新的 isLogin，避免闭包问题
  const isLoginRef = useRef(isLogin);
  // 使用 ref 存储最新的 currentTab，避免闭包问题
  const currentTabRef = useRef(currentTab);
  // 防止并发执行的标志
  const isSendingRef = useRef(false);

  // 同步更新 isLoginRef
  useEffect(() => {
    isLoginRef.current = isLogin;
  }, [isLogin]);

  // 同步更新 currentTabRef
  useEffect(() => {
    currentTabRef.current = currentTab;
  }, [currentTab]);

  // 计算下次打点时间并设置定时器（递归调用，避免累积误差）
  const scheduleNextHeartbeat = useCallback(() => {
    // 防止重复调度：如果已有定时器，直接返回（无论 tab 是否可见）
    if (timeoutRef.current) {
      return;
    }

    // 如果 tab 不可见，不设置定时器
    if (!isVisibleRef.current) {
      return;
    }

    if (typeof window === 'undefined') {
      return;
    }

    const now = Date.now();
    const lastTimeStr = getLocalStorageItem(HEARTBEAT_STORAGE_KEY);
    const lastTime = lastTimeStr ? parseInt(lastTimeStr, 10) : 0;

    // 计算距离下次打点的时间（毫秒）
    const timeSinceLastBeat = now - lastTime;
    const timeUntilNextBeat = HEARTBEAT_INTERVAL * 1000 - timeSinceLastBeat;

    // 发送心跳并调度下一次
    const sendAndSchedule = async () => {
      // 防止并发执行：如果正在发送，直接返回
      if (isSendingRef.current) {
        return;
      }

      // 设置发送标志
      isSendingRef.current = true;

      try {
        // 使用 ref 获取最新的 isLogin 和 currentTab 值，避免闭包问题
        const currentPage = currentTabRef.current || '';

        await track('heart_beat', {
          heartbeat_interval: HEARTBEAT_INTERVAL,
          is_login: isLoginRef.current,
          current_page: currentPage,
        });
        // track 完成后记录实际发送时间，避免累积误差
        const sendTime = Date.now();
        setLocalStorageItem(HEARTBEAT_STORAGE_KEY, sendTime.toString());
      } catch (error) {
        // 即使发送失败，也记录时间，避免重复发送
        const sendTime = Date.now();
        setLocalStorageItem(HEARTBEAT_STORAGE_KEY, sendTime.toString());
        console.error('sendAndSchedule Failed:', error);
      } finally {
        // 无论成功失败，都重置发送标志
        isSendingRef.current = false;
      }

      // 清除当前的 timeout，准备设置下一次
      timeoutRef.current = null;

      // 递归调用，设置下一次定时器（基于实际发送时间，避免累积误差）
      scheduleNextHeartbeat();
    };

    if (timeUntilNextBeat <= 0) {
      // 如果已经超过5分钟，立即发送
      // 使用一个临时的占位符来防止并发调用（在 sendAndSchedule 完成前阻止重复调度）
      timeoutRef.current = setTimeout(sendAndSchedule, 0);
    } else {
      // 设置定时器在正确的时间发送
      timeoutRef.current = setTimeout(sendAndSchedule, timeUntilNextBeat);
    }
  }, []); // 移除 clearTimer 依赖，直接内联清除逻辑

  // 处理页面可见性变化
  useEffect(() => {
    const handleVisibilityChange = () => {
      const isVisible = !document.hidden;
      isVisibleRef.current = isVisible;

      if (isVisible) {
        // tab 恢复可见时，重新调度下次打点（会自动检查是否需要立即发送）
        scheduleNextHeartbeat();
      } else {
        // tab 不可见时，清除定时器
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [scheduleNextHeartbeat]);

  // 初始化心跳打点（仅在组件挂载时初始化一次）
  useEffect(() => {
    // 初始化时调度第一次打点
    scheduleNextHeartbeat();

    return () => {
      // 清理定时器
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 仅在挂载时执行一次，isLogin 和 currentTab 的变化通过 ref 同步
}

